<template>
  <select
    :id="id"
    :aria-describedby="ariaDescribedby || undefined"
    :aria-invalid="ariaInvalid || undefined"
    :aria-label="ariaLabel || undefined"
    class="base-select"
    :disabled="disabled"
    :required="required"
    :value="modelValue"
    @change="handleChange"
  >
    <option
      v-if="placeholder"
      disabled
      value=""
    >
      {{ placeholder }}
    </option>
    <template
      v-for="entry in options"
      :key="'group' in entry ? entry.group : entry.value"
    >
      <optgroup
        v-if="'group' in entry"
        :label="entry.group"
      >
        <option
          v-for="opt in entry.options"
          :key="opt.value"
          :value="opt.value"
        >
          {{ opt.label }}
        </option>
      </optgroup>
      <option
        v-else
        :value="entry.value"
      >
        {{ entry.label }}
      </option>
    </template>
  </select>
</template>

<script setup lang="ts">
/**
 * Option structure for select dropdown
 */
export interface SelectOption {
  /** Value emitted when option is selected */
  value: string | number;
  /** Display text shown in dropdown */
  label: string;
}

/**
 * Grouped option structure for rendering `<optgroup>` in select dropdown
 */
export interface SelectOptionGroup {
  /** Label for the option group (shown as the group header) */
  group: string;
  /** Options within the group */
  options: SelectOption[];
}

/** Either a flat option or a named group of options */
export type SelectEntry = SelectOption | SelectOptionGroup;

/**
 * Dropdown select component with v-model support
 *
 * Provides a styled select field with support for custom options,
 * placeholders, and accessibility features.
 *
 * @example Basic select
 * ```vue
 * <BaseSelect
 *   v-model="country"
 *   :options="[
 *     { value: 'us', label: 'United States' },
 *     { value: 'ca', label: 'Canada' },
 *     { value: 'uk', label: 'United Kingdom' }
 *   ]"
 *   placeholder="Select country"
 * />
 * ```
 *
 * @example With number values
 * ```vue
 * <BaseSelect
 *   v-model="quantity"
 *   :options="[
 *     { value: 1, label: 'One' },
 *     { value: 5, label: 'Five' },
 *     { value: 10, label: 'Ten' }
 *   ]"
 *   required
 * />
 * ```
 *
 * @example With accessibility
 * ```vue
 * <BaseSelect
 *   v-model="status"
 *   :options="statusOptions"
 *   aria-label="Plan status"
 *   aria-describedby="status-hint"
 * />
 * ```
 */
export interface BaseSelectProps {
  /** Current selected value (use with v-model) */
  modelValue: string | number;

  /** Array of options to display in dropdown (flat or grouped) */
  options: SelectEntry[];

  /**
   * Placeholder shown as disabled first option when no value selected
   * Requires modelValue to be empty string for placeholder to show
   */
  placeholder?: string;

  /**
   * Disables the select and prevents interaction
   * @default false
   */
  disabled?: boolean;

  /**
   * Marks field as required for form validation
   * @default false
   */
  required?: boolean;

  /** Unique element ID for label association */
  id?: string;

  /** Accessible label for screen readers (use when visual label not present) */
  ariaLabel?: string;

  /** ID of element describing this select (typically hint message ID) */
  ariaDescribedby?: string;

  /** Indicates the select has an error for accessibility */
  ariaInvalid?: boolean;
}

const props = withDefaults(defineProps<BaseSelectProps>(), {
  disabled: false,
  required: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: string | number];
}>();

const handleChange = (event: Event) => {
  const target = event.target as HTMLSelectElement;
  const raw = target.value;

  for (const entry of props.options) {
    if ('group' in entry) {
      const match = entry.options.find(o => String(o.value) === raw);
      if (match) {
        emit('update:modelValue', match.value);
        return;
      }
    } else if (String(entry.value) === raw) {
      emit('update:modelValue', entry.value);
      return;
    }
  }

  console.warn(`[BaseSelect] No option matched value "${raw}" — emit suppressed.`);
};
</script>

<style>
.base-select {
  width: var(--input-width);
  padding: var(--input-padding);
  line-height: var(--input-line-height);
  border-width: var(--input-border-width-top) var(--input-border-width-right) var(--input-border-width-bottom)
    var(--input-border-width-left);
  border-style: solid;
  border-color: var(--input-border-color);
  border-radius: var(--input-border-radius-left) var(--input-border-radius-right) var(--input-border-radius-right)
    var(--input-border-radius-left);
  font-family: var(--input-font-family);
  font-size: var(--input-font-size);
  background-color: var(--input-background);
  transition: var(--input-transition);
  height: calc(1.5rem + 2px + var(--gap-xs) * 2);
  cursor: pointer;

  &:focus-visible {
    outline: var(--focus-ring-width) solid var(--focus-ring-color);
    outline-offset: var(--focus-ring-offset);
  }

  &:disabled {
    background-color: var(--input-disabled-background);
    cursor: not-allowed;
    opacity: var(--input-disabled-opacity);
  }

  option[disabled] {
    color: var(--color-neutral-500);
  }
}
</style>
